import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Portal } from './Portal';
import { useT } from '../i18n';

interface Plan {
  id: string;
  name: string;
  maxUsers: number;
  maxContacts: number;
  maxCampaigns: number;
  maxConnections: number;
  maxMessagesPerMonth: number;
}

export interface SubscriptionState {
  tenantId: string;
  name: string;
  status: string;
  plan: string;
  planName: string;
  trialEndsAt: string | null;
  paidUntil: string | null;
  expiresAt: string | null;
  daysLeft: number | null;
  messagesSent: number;
  messagesLimit: number;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

function toDateInput(value: string | null): string {
  return value ? new Date(value).toISOString().slice(0, 10) : '';
}

export function PlanManagerModal({ subscription, onClose, onSaved }: {
  subscription: SubscriptionState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planId, setPlanId] = useState(subscription.plan);
  const [status, setStatus] = useState(subscription.status);
  const [paidUntil, setPaidUntil] = useState(toDateInput(subscription.paidUntil));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch('/api/subscription/plans', { headers: authHeaders() })
      .then(response => response.json())
      .then(data => setPlans(data.plans || []))
      .catch(() => setPlans([]));
  }, []);

  const selected = plans.find(plan => plan.id === planId);

  const addMonth = () => {
    const base = paidUntil ? new Date(paidUntil) : new Date();
    base.setMonth(base.getMonth() + 1);
    setPaidUntil(base.toISOString().slice(0, 10));
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await fetch(`/api/subscription/tenants/${subscription.tenantId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          planId,
          status,
          // Fim do dia escolhido, para o cliente aproveitar o último dia inteiro
          paidUntil: paidUntil ? new Date(`${paidUntil}T23:59:59`).toISOString() : null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
      toast.success(t('plan.updated'));
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('plan.errUpdate'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{t('plan.title')}</h3>
              <p className="text-sm text-gray-500" dir="auto">{subscription.name}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2">✕</button>
          </div>

          <div className="p-6 space-y-4">
            <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700 flex justify-between">
              <span>{t('plan.usedThisMonth')}</span>
              <span className="font-semibold">
                {t('plan.messagesOf', {
                  used: subscription.messagesSent.toLocaleString('en-US'),
                  limit: subscription.messagesLimit > 0 ? subscription.messagesLimit.toLocaleString('en-US') : t('plan.unlimited'),
                })}
              </span>
            </div>

            <div>
              <label htmlFor="plan" className="block text-sm font-medium text-gray-700 mb-1">{t('plan.plan')}</label>
              <select id="plan" value={planId} onChange={event => setPlanId(event.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                {plans.map(plan => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
              </select>
              {selected && (
                <p className="text-xs text-gray-500 mt-2">
                  {selected.maxMessagesPerMonth > 0
                    ? t('plan.perMonth', { count: selected.maxMessagesPerMonth.toLocaleString('en-US') })
                    : t('plan.unlimitedMessages')}{' · '}
                  {t('plan.summary', {
                    contacts: selected.maxContacts.toLocaleString('en-US'),
                    connections: selected.maxConnections,
                    users: selected.maxUsers,
                  })}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">{t('plan.status')}</label>
              <select id="status" value={status} onChange={event => setStatus(event.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="TRIAL">{t('plan.statusTrial')}</option>
                <option value="ACTIVE">{t('plan.statusActive')}</option>
                <option value="SUSPENDED">{t('plan.statusSuspended')}</option>
              </select>
            </div>

            <div>
              <label htmlFor="paidUntil" className="block text-sm font-medium text-gray-700 mb-1">{t('plan.paidUntil')}</label>
              <div className="flex gap-2">
                <input
                  id="paidUntil"
                  type="date"
                  value={paidUntil}
                  onChange={event => setPaidUntil(event.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                />
                <button type="button" onClick={addMonth} className="px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200">{t('plan.addMonth')}</button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('plan.paidUntilHelp')}</p>
            </div>
          </div>

          <div className="px-6 py-4 border-t flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">{t('common.cancel')}</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50">
              {saving ? t('plan.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
